import { ChangeDetectionStrategy, Component, computed, CUSTOM_ELEMENTS_SCHEMA, inject, signal } from '@angular/core';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { BusinessServices, ReferralStatus, Roles } from '@shs-digi-care-monorepo/interfaces';
import { ReferredOutComponent } from '../../sh-referred-out/sh-referred-out.component';
import { ShAssigneePickerComponent } from '../../sh-assignee-picker/sh-assignee-picker.component';
import { AssigneeValue } from '../../sh-assignee-picker/model/sh-assignee-picker.model';
import { LocalizedDatePipe } from '../../../pipes';
import type { Referral } from '../model/referral.model';
import { resolveAssignmentSelection } from '../model/assignment-selection.model';
import type { AssignmentSelection } from '../model/assignment-selection.model';

export interface ReferralDetailDialogData {
    referral: Referral;
    parentDisplayId: string;
    isTaskOwner: boolean;
    supportsReferralReassignment: boolean;
    businessService?: BusinessServices;
    roles?: Roles[];
}

export interface ReferralReassignmentPayload {
    referredOutEmail?: string;
    referredOutTeam?: string;
}

export type ReferralDetailDialogResult =
    | { action: 'closeReferral' }
    | { action: 'reassignReferral'; payload: ReferralReassignmentPayload };

@Component({
    selector: 'sh-referral-detail-dialog',
    templateUrl: './referral-detail-dialog.component.html',
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [TranslateModule, LocalizedDatePipe, ReactiveFormsModule, ReferredOutComponent, ShAssigneePickerComponent],
    schemas: [CUSTOM_ELEMENTS_SCHEMA]
})
export class ReferralDetailDialogComponent {
    private readonly dialogRef =
        inject<MatDialogRef<ReferralDetailDialogComponent, ReferralDetailDialogResult>>(MatDialogRef);
    public readonly data = inject<ReferralDetailDialogData>(MAT_DIALOG_DATA);
    private readonly translateService = inject(TranslateService);

    protected readonly isTaskOwner = computed(() => this.data.isTaskOwner);
    protected readonly isClosed = computed(() => this.data.referral.status === ReferralStatus.CLOSED);
    protected readonly isInternalReferredOut = computed(() => !!this.data.referral.isInternalReferredOut);
    protected readonly supportsReassignment = computed(
        () => this.data.supportsReferralReassignment && !this.isClosed()
    );

    protected readonly editing = signal(false);

    protected readonly assigneeControl = new FormControl<AssigneeValue | null>(null);
    protected readonly emailControl = new FormControl<string>(this.data.referral.referredOutEmail ?? '');

    protected readonly initialEffectiveTarget = computed<AssignmentSelection>(() => {
        const email = this.data.referral.referredOutEmail?.trim() || null;
        const team = this.data.referral.referredOutTeam || null;
        return resolveAssignmentSelection(email, team);
    });

    protected readonly currentEffectiveTarget = computed<AssignmentSelection>(() => {
        if (this.isInternalReferredOut()) {
            const assignee = this.assigneeControl.value;
            if (!assignee) {
                return { type: null, value: null };
            }
            if (assignee.type === 'team') {
                return { type: 'team', value: assignee.id };
            }
            return { type: 'email', value: assignee.email ?? assignee.id };
        }
        const email = this.emailControl.value?.trim() || null;
        return resolveAssignmentSelection(email, null);
    });

    protected readonly hasEffectiveChange = computed(() => {
        const current = this.currentEffectiveTarget();
        const initial = this.initialEffectiveTarget();
        return current.type !== initial.type || current.value !== initial.value;
    });

    protected readonly statusLabel = computed(() => {
        const key =
            this.data.referral.status === ReferralStatus.OPEN
                ? 'request.requestDetail.referral-menu.referral-status-active'
                : 'request.requestDetail.referral-menu.referral-status-closed';
        return this.translateService.instant(key) as string;
    });

    protected toggleEditing(): void {
        if (!this.supportsReassignment()) {
            return;
        }
        this.editing.update((v) => !v);
        if (!this.editing()) {
            // Reset to initial values on cancel
            this.assigneeControl.reset(null);
            this.emailControl.setValue(this.data.referral.referredOutEmail ?? '');
        }
    }

    protected submitReassignment(): void {
        if (this.isInternalReferredOut()) {
            const assignee = this.assigneeControl.value;
            if (!assignee) {
                return;
            }
            const payload: ReferralReassignmentPayload =
                assignee.type === 'team'
                    ? { referredOutTeam: assignee.id }
                    : { referredOutEmail: assignee.email ?? assignee.id };
            this.dialogRef.close({ action: 'reassignReferral', payload });
        } else {
            const email = this.emailControl.value?.trim();
            if (!email) {
                return;
            }
            this.dialogRef.close({ action: 'reassignReferral', payload: { referredOutEmail: email } });
        }
    }

    public closeReferral(): void {
        this.dialogRef.close({ action: 'closeReferral' });
    }

    public cancel(): void {
        this.dialogRef.close();
    }
}
